import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

// Image that drifts with scroll; wrapper is oversized so edges never show.
export const ParallaxImage = ({ src, srcSet, sizes, alt, className = "", imgClassName = "", range = 8 }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${range}%`, `${range}%`]);
  return (
    <div ref={ref} className={`absolute inset-0 overflow-hidden ${className}`}>
      <motion.div style={{ y: reduce ? 0 : y }} className="absolute -inset-y-[12%] inset-x-0">
        <img src={src} srcSet={srcSet} sizes={sizes} alt={alt} loading="lazy" decoding="async" className={`w-full h-full object-cover object-center ${imgClassName}`} />
      </motion.div>
    </div>
  );
};

// Card that swings open like a booklet page as it scrolls into view.
export const BookPage = ({ children, className = "", hinge = "left" }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start 35%"] });
  const angle = { left: -38, right: 38, bottom: 32 }[hinge];
  const rot = useTransform(scrollYProgress, [0, 1], [angle, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [0.3, 1]);
  const origin = { left: "left center", right: "right center", bottom: "center bottom" }[hinge];
  const style = reduce ? {} : { [hinge === "bottom" ? "rotateX" : "rotateY"]: rot, opacity, transformOrigin: origin };
  return (
    <div ref={ref} className={className} style={{ perspective: 1800 }}>
      <motion.div style={style} className="h-full will-change-transform">{children}</motion.div>
    </div>
  );
};

// Sets --mx/--my on the element so a CSS spotlight can follow the cursor.
export const trackSpotlight = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

export const Spotlight = ({ color = "rgba(219,150,13,0.06)" }) => (
  <div
    className="pointer-events-none absolute inset-0 z-[1] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
    style={{ background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 50%), ${color}, transparent 65%)` }}
  />
);
